import { Immutable } from "immer";
import React from "react";

import type { HomeMultiProdModule as HomeMultiProdModuleValue } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "../../styles/composable/category-callout.module.scss";

/**
 * tempurpedic ``multi_prod_module`` (``HomeMultiProdModule``) -- the DEPRECATED
 * homepage category-callout module (#33588 HomePage ``modules`` consolidation).
 *
 * REFACTOR (legacy ``home-multi-prod-module.html``): the ``category-callout``
 * wrapper (title + desc), then a list of category tiles
 * (image + slugified-analytics CTA button linking to the chosen page). Styles
 * come from the shared category-callout composable module. The
 * legacy ``{% wagtail_image ... specs='width-200,width-300,width-400' %}``
 * responsive image is rendered as the largest eager rendition (the shipped
 * eager-image convention; the ``responsive-img`` class is kept).
 */
type IProps = Immutable<{ value: HomeMultiProdModuleValue }>;

// Mirror of django.utils.text.slugify (allow_unicode=False); the legacy used
// ``category.product_cta|slugify|lower`` (the trailing ``|lower`` is redundant
// -- slugify already lowercases).
const slugify = (value: string): string =>
    value
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-z0-9\s-]/g, "")
        .trim()
        .replace(/[\s_-]+/g, "-")
        .replace(/^-+|-+$/g, "");

export const HomeMultiProdModule = (props: IProps): React.JSX.Element => {
    const value = props.value;
    return (
        <div
            className={concatClassNames([
                styles.categoryCallout,
                "category-callout",
                "l-capped-width",
                "u-margin-bottom--xl",
            ])}
        >
            <div className={styles.categoryCalloutCopy}>
                <h2 className={styles.categoryCalloutTitle}>{value.title}</h2>
                <div className={styles.categoryCalloutDescription}>
                    {value.desc}
                </div>
            </div>

            <ul className={styles.categoryCalloutList}>
                {(value.categories ?? []).map((category, index) => {
                    const cta = category.product_cta ?? "";
                    const ctaSlug = slugify(cta);
                    const image = category.product_image;
                    const imageUrl = image
                        ? (image.renditions["width-400"]?.url ?? image.url)
                        : "";
                    return (
                        <li
                            key={index}
                            className={styles.categoryCalloutListItem}
                        >
                            <div
                                className={
                                    styles.categoryCalloutListImageContainer
                                }
                            >
                                <img
                                    className="responsive-img"
                                    src={imageUrl}
                                    alt={image?.default_alt_text ?? ""}
                                    width={image?.width}
                                    height={image?.height}
                                    loading="lazy"
                                    decoding="async"
                                />
                            </div>
                            <a
                                href={category.page?.url ?? ""}
                                className={concatClassNames([
                                    "button",
                                    styles.categoryCalloutListButton,
                                    "al-hp-category-callout__list-button",
                                    `al-hp-category-callout__list-button--${ctaSlug}`,
                                ])}
                            >
                                {cta}
                            </a>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};
